import React, { useState, useEffect, useCallback, useRef } from "react";
import { API_URL, axios, LOGO_URL } from "../constants";
import { Link } from "react-router-dom";
import { LogoWatermark } from "../components/landing/HeroSections";
import { Footer } from "../components/landing/InteractiveSections";
import { ArrowLeft, LogOut } from "lucide-react";
import AddOnPopup from "../components/AddOnPopup";
import { CancelBookingModal, FeedbackModal } from "../components/portal/CancelBookingModal";
import { InviteModal, EditBookingModal } from "../components/portal/InviteModal";
import SmartInviteModal from "../components/invites/SmartInviteModal";
import MyInvitesDashboard from "../components/invites/MyInvitesDashboard";
import BookingsList from "../components/portal/BookingsList";
import PrivacyPanel from "../components/portal/PrivacyPanel";
import { AddressManager } from "../components/portal/AddressManager";
import { MyDecorations } from "../components/portal/MyDecorations";
import MyCreationsTab from "../components/portal/MyCreationsTab";
import { CustomerLoginScreen } from "../components/portal/CustomerLoginScreen";
import { CustomerDashboardHero, CustomerDashboardCTAs } from "../components/portal/CustomerDashboardHero";
import AccountSecurityCard from "../components/AccountSecurityCard";
import { logger } from "../utils/logger";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
// Strip "www." so Emergent auth shows "DoozyGo" not "Www" as app name
const getNormalizedOrigin = () => window.location.origin.replace('://www.', '://');
const getGoogleAuthUrl = () => {
  return `https://auth.emergentagent.com/?redirect=${encodeURIComponent(getNormalizedOrigin() + '/my-account')}`;
};
const FB_APP_ID = process.env.REACT_APP_FACEBOOK_APP_ID || "1983043869763915";

import { getCustomerToken, setCustomerToken, clearCustomerToken, getCustomerAuthHeaders } from "../utils/customerAuth";

/* ─── Auth helper: unified customer auth (localStorage w/ sessionStorage mirror) ─── */
const SESSION_KEY = "cs_tok"; // legacy — kept for the pre-React inline bootstrap script only
const getAuthHeaders = () => getCustomerAuthHeaders();
const saveSession = (token) => setCustomerToken(token);
const clearSession = () => clearCustomerToken();

/* ─── Floating animated balloon SVG ─── */
const BalloonIcon = ({ active, index, delay = 0 }) => (
  <div
    className={`relative transition-all duration-500 ${active ? 'scale-110' : 'scale-100'}`}
    style={active ? { animation: `floatBalloon 3s ease-in-out ${delay}s infinite` } : {}}
  >
    <svg width="36" height="48" viewBox="0 0 36 48" fill="none">
      <ellipse cx="18" cy="17" rx="14" ry="17" fill={active ? "#E83E8C" : "#CBD5E1"} opacity={active ? 1 : 0.35} />
      <ellipse cx="18" cy="17" rx="14" ry="17" fill="url(#shine)" opacity="0.3" />
      <path d="M18 34 L18 46" stroke={active ? "#E83E8C" : "#CBD5E1"} strokeWidth="1.2" opacity={active ? 0.6 : 0.25} />
      <path d="M15 46 Q18 42 21 46" stroke={active ? "#E83E8C" : "#CBD5E1"} strokeWidth="1" fill="none" opacity={active ? 0.6 : 0.25} />
      {active && <text x="18" y="21" textAnchor="middle" fontSize="11" fontWeight="bold" fill="white">{index + 1}</text>}
      {!active && <text x="18" y="21" textAnchor="middle" fontSize="10" fill="#94a3b8">{index + 1}</text>}
      <defs><radialGradient id="shine" cx="0.35" cy="0.3"><stop offset="0%" stopColor="white" /><stop offset="100%" stopColor="transparent" /></radialGradient></defs>
    </svg>
  </div>
);

/* ─── CSS keyframes injected once ─── */
const BalloonStyles = () => (
  <style>{`
    @keyframes floatBalloon {
      0%, 100% { transform: translateY(0) scale(1.1); }
      50% { transform: translateY(-8px) scale(1.15); }
    }
  `}</style>
);

export default function CustomerPortal() {
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState("");
  const [authMode, setAuthMode] = useState("select");
  const [phoneInput, setPhoneInput] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [otpPhone, setOtpPhone] = useState("");
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpError, setOtpError] = useState("");
  const [otpCooldown, setOtpCooldown] = useState(0);
  const [editingPhone, setEditingPhone] = useState(false);
  const [editPhoneInput, setEditPhoneInput] = useState("");
  // Cancel flow states
  const [cancelView, setCancelView] = useState(null); // null | {booking}
  const [cancelReason, setCancelReason] = useState("");
  const [cancelCustomReason, setCancelCustomReason] = useState("");
  const [cancelStep, setCancelStep] = useState("select"); // select | confirm | done
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelError, setCancelError] = useState("");
  const [editRequest, setEditRequest] = useState(null);
  const [editChanges, setEditChanges] = useState("");
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [expandedBookingId, setExpandedBookingId] = useState(null);
  const [cancelPreview, setCancelPreview] = useState(null);
  // Feedback popup
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackText, setFeedbackText] = useState("");
  const [feedbackRating, setFeedbackRating] = useState(0);
  const [feedbackSent, setFeedbackSent] = useState(false);
  const [cancelledBookingId, setCancelledBookingId] = useState("");
  // Invite modal
  const [inviteBooking, setInviteBooking] = useState(null);
  const [smartInviteBooking, setSmartInviteBooking] = useState(null);
  const [inviteText, setInviteText] = useState("");
  const [inviteCopied, setInviteCopied] = useState(false);
  // Address management
  const [addresses, setAddresses] = useState([]);
  const [editingAddr, setEditingAddr] = useState(null); // address_id or 'new'
  const [addrForm, setAddrForm] = useState({ label: 'Home', address: '', pincode: '' });
  const [addrLoading, setAddrLoading] = useState(false);
  const [addOnBooking, setAddOnBooking] = useState(null); // booking object to add add-ons to
  const [addOnSubmitting, setAddOnSubmitting] = useState(false);

  const hasProcessed = useRef(false);
  const sendingRef = useRef(false);

  const fetchProfile = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/customer-auth/me`, { withCredentials: true, headers: getAuthHeaders() });
      setCustomer(res.data);
      setEditPhoneInput(res.data.phone || "");
      // Fetch addresses too
      try {
        const addrRes = await axios.get(`${API_URL}/api/customer-auth/addresses`, { withCredentials: true, headers: getAuthHeaders() });
        setAddresses(addrRes.data.addresses || []);
      } catch (err) { logger.error('[Portal] Failed to fetch addresses:', err.message); }
      // If user was redirected here from booking flow, send them back
      const params = new URLSearchParams(window.location.search);
      const returnUrl = localStorage.getItem("doozygo_return_url");
      const returnToBooking = params.get("returnTo") === "booking" || sessionStorage.getItem("doozygo_return_to_booking") === "true" || !!returnUrl;
      if (returnToBooking) {
        sessionStorage.removeItem("doozygo_return_to_booking");
        const dest = returnUrl || "/#book-online";
        localStorage.removeItem("doozygo_return_url");
        window.history.replaceState({}, "", "/my-account");
        window.location.href = dest;
        return;
      }
    } catch {
      clearSession();
      setCustomer(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (hasProcessed.current) return;
    
    // Check 1: Pre-React inline script may have already exchanged the token
    if (window.__DOOZYGO_AUTH__ && window.__DOOZYGO_AUTH__.session_token) {
      hasProcessed.current = true;
      saveSession(window.__DOOZYGO_AUTH__.session_token);
      delete window.__DOOZYGO_AUTH__;
      fetchProfile();
      return;
    }
    
    const hash = window.location.hash;
    
    // Check 2: Google OAuth redirect: #session_id=...
    if (hash && hash.includes("session_id=")) {
      hasProcessed.current = true;
      const sessionId = hash.split("session_id=")[1]?.split("&")[0];
      if (sessionId) {
        window.history.replaceState({}, "", "/my-account");
        // Use GET endpoint (faster, no CORS preflight)
        axios.get(`${API_URL}/api/customer-auth/google-callback`, { params: { session_id: sessionId }, withCredentials: true })
          .then((res) => { saveSession(res.data.session_token); fetchProfile(); })
          .catch((err) => { setAuthError(err.response?.data?.detail || "Google login failed. Please try again."); setLoading(false); });
        return;
      }
    }
    
    // Check 3: Facebook OAuth redirect: #access_token=...
    if (hash && hash.includes("access_token=")) {
      hasProcessed.current = true;
      const accessToken = hash.split("access_token=")[1]?.split("&")[0];
      if (accessToken) {
        window.history.replaceState({}, "", "/my-account");
        axios.get(`${API_URL}/api/customer-auth/facebook-callback`, { params: { access_token: accessToken }, withCredentials: true })
          .then((res) => { saveSession(res.data.session_token); fetchProfile(); })
          .catch((err) => { setAuthError(err.response?.data?.detail || "Facebook login failed. Please try again."); setLoading(false); });
        return;
      }
    }
    
    // Check 4: Pre-React script might still be in-flight — check session
    const preAuthToken = getCustomerToken();
    if (preAuthToken && !hash) {
      // Token exists in sessionStorage (possibly set by pre-React script)
      fetchProfile();
      return;
    }
    
    fetchProfile();
  }, [fetchProfile]);

  // FB SDK no longer needed — using redirect-based OAuth instead of popup

  useEffect(() => {
    if (otpCooldown <= 0) return;
    const timer = setTimeout(() => setOtpCooldown(c => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [otpCooldown]);

  const handleFacebookLogin = () => {
    setAuthError("");
    // Use redirect flow (works on Safari + all browsers, unlike popup which Safari blocks)
    // Use non-www origin to match Facebook OAuth redirect URI whitelist
    const redirectUri = encodeURIComponent(getNormalizedOrigin() + '/my-account');
    window.location.href = `https://www.facebook.com/v19.0/dialog/oauth?client_id=${FB_APP_ID}&redirect_uri=${redirectUri}&response_type=token&scope=public_profile`;
  };

  const handleSendOtp = async () => {
    if (sendingRef.current) return;
    const cleaned = phoneInput.replace(/\D/g, "").slice(-10);
    if (cleaned.length !== 10) { setOtpError("Enter a valid 10-digit mobile number"); return; }
    sendingRef.current = true; setOtpSending(true); setOtpError("");
    const reqId = `otp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    try {
      await axios.post(`${API_URL}/api/customer-auth/phone/send-otp`, { phone: cleaned, request_id: reqId });
      setOtpPhone(cleaned); setAuthMode("otp"); setOtpCooldown(90);
    } catch (e) { setOtpError(e.response?.data?.detail || "Failed to send OTP."); }
    finally { setOtpSending(false); sendingRef.current = false; }
  };

  const handleResendOtp = async () => {
    if (sendingRef.current || otpCooldown > 0) return;
    sendingRef.current = true; setOtpSending(true); setOtpError(""); setOtpCode("");
    const reqId = `otp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    try { await axios.post(`${API_URL}/api/customer-auth/phone/send-otp`, { phone: otpPhone, force: true, request_id: reqId }); setOtpCooldown(90); }
    catch (e) { setOtpError(e.response?.data?.detail || "Failed to resend OTP."); }
    finally { setOtpSending(false); sendingRef.current = false; }
  };

  const handleVerifyOtp = async () => {
    if (otpCode.length < 4) { setOtpError("Enter the OTP code"); return; }
    setOtpVerifying(true); setOtpError("");
    try { const res = await axios.post(`${API_URL}/api/customer-auth/phone/verify-otp`, { phone: otpPhone, code: otpCode }, { withCredentials: true }); saveSession(res.data.session_token); fetchProfile(); }
    catch (e) {
      const msg = e.response?.data?.detail || "Invalid OTP.";
      setOtpError(msg);
      setOtpVerifying(false);
      // If OTP expired, reset cooldown so user can resend immediately
      if (msg.toLowerCase().includes("expired") || msg.toLowerCase().includes("new code")) {
        setOtpCooldown(0);
      }
    }
  };

  const handleLogout = async () => {
    try { await axios.post(`${API_URL}/api/customer-auth/logout`, {}, { withCredentials: true, headers: getAuthHeaders() }); } catch (err) { logger.error('[Portal] Logout error:', err.message); }
    clearSession();
    setCustomer(null); setAuthMode("select"); setAuthError("");
  };

  const handleEditBookingSubmit = async () => {
    if (!editChanges.trim()) return;
    setEditSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/customer-auth/edit-booking-request`, { booking_id: editRequest.id, changes: editChanges }, { withCredentials: true, headers: getAuthHeaders() });
      setEditRequest(null); setEditChanges("");
      alert("Edit request submitted! Our team will contact you shortly via WhatsApp/Email.");
    } catch (e) { alert(e.response?.data?.detail || "Failed to submit. Please try again."); }
    setEditSubmitting(false);
  };

  const handleUpdatePhone = async () => {
    try { await axios.put(`${API_URL}/api/customer-auth/profile`, { phone: editPhoneInput }, { withCredentials: true, headers: getAuthHeaders() }); setEditingPhone(false); fetchProfile(); } catch (err) { logger.error('[Portal] Update phone error:', err.message); }
  };

  // ── Cancel flow handlers ──
  const startCancel = (booking) => {
    setCancelView(booking); setCancelStep("select"); setCancelReason(""); setCancelCustomReason(""); setCancelError(""); setCancelPreview(null);
  };

  const handleCancelPreview = async () => {
    if (!cancelReason) { setCancelError("Please select a reason"); return; }
    setCancelLoading(true); setCancelError("");
    try {
      const phone = customer?.phone || "";
      const res = await axios.post(`${API_URL}/api/bookings/cancel-preview`, { booking_id: cancelView.id, phone: phone.replace(/\D/g, "").slice(-10) });
      setCancelPreview(res.data); setCancelStep("confirm");
    } catch (err) { setCancelError(err.response?.data?.detail || "Unable to preview cancellation. Please try calling us."); }
    finally { setCancelLoading(false); }
  };

  const handleConfirmCancel = async () => {
    setCancelLoading(true); setCancelError("");
    const finalReason = cancelReason === "Other reason" ? cancelCustomReason || "Other reason" : cancelReason;
    try {
      const phone = customer?.phone || "";
      await axios.post(`${API_URL}/api/bookings/cancel-confirm`, { booking_id: cancelView.id, phone: phone.replace(/\D/g, "").slice(-10), reason: finalReason });
      setCancelStep("done"); setCancelledBookingId(cancelView.id);
    } catch (err) { setCancelError(err.response?.data?.detail || "Failed to cancel. Please call us."); }
    finally { setCancelLoading(false); }
  };

  const openFeedbackPopup = () => { setShowFeedback(true); setFeedbackText(""); setFeedbackRating(0); setFeedbackSent(false); };

  const submitFeedback = async () => {
    if (!feedbackText.trim()) return;
    try {
      await axios.post(`${API_URL}/api/customer-auth/cancellation-feedback`, { booking_id: cancelledBookingId, feedback: feedbackText, rating: feedbackRating });
      setFeedbackSent(true);
    } catch (err) { logger.error('[Portal] Feedback submit error:', err.message); }
  };

  // ── Invite Friends handlers ──
  const openInviteModal = (booking) => {
    const dateStr = new Date(booking.date).toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    const name = customer?.name || "We";
    const defaultText = `You're Invited!\n\n${name} ${name === "We" ? "are" : "is"} hosting a ${booking.occasion} party and would love for you to be there!\n\nDate: ${dateStr}\nHosted by: ${name}\nPowered by: DoozyGo® - Premium House Party Setups\n\nWe can't wait to celebrate with you!`;
    setInviteText(defaultText);
    setInviteBooking(booking);
    setInviteCopied(false);
  };

  const shareInviteWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(inviteText)}`, '_blank');
  };

  const shareInviteEmail = () => {
    const subject = encodeURIComponent(`You're Invited to a ${inviteBooking?.occasion || 'Party'}!`);
    const body = encodeURIComponent(inviteText);
    window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
  };

  const copyInviteText = () => {
    navigator.clipboard.writeText(inviteText).then(() => { setInviteCopied(true); setTimeout(() => setInviteCopied(false), 2000); });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#FFFAF5] to-[#FFF0E8] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[#E83E8C] border-t-transparent rounded-full" />
      </div>
    );
  }

  /* ════════════════════════════════════════════
     NOT LOGGED IN — Login Page
     ════════════════════════════════════════════ */
  if (!customer) {
    return (
      <CustomerLoginScreen
        authError={authError} setAuthError={setAuthError}
        authMode={authMode} setAuthMode={setAuthMode}
        getGoogleAuthUrl={getGoogleAuthUrl} handleFacebookLogin={handleFacebookLogin}
        phoneInput={phoneInput} setPhoneInput={setPhoneInput}
        otpCode={otpCode} setOtpCode={setOtpCode} otpPhone={otpPhone}
        otpSending={otpSending} otpVerifying={otpVerifying} otpError={otpError}
        otpCooldown={otpCooldown}
        handleSendOtp={handleSendOtp} handleResendOtp={handleResendOtp} handleVerifyOtp={handleVerifyOtp}
      />
    );
  }

  /* ════════════════════════════════════════════
     LOGGED IN — Dashboard
     ════════════════════════════════════════════ */
  const balloonProgress = Math.min(customer.loyalty_balloons || 0, 10);
  const freePartyEarned = customer.free_party_earned || balloonProgress >= 10;

  // Address handlers
  const handleSaveAddress = async () => {
    if (!addrForm.address.trim()) return;
    setAddrLoading(true);
    try {
      if (editingAddr && editingAddr !== 'new') {
        await axios.put(`${API_URL}/api/customer-auth/addresses/${editingAddr}`, addrForm, { withCredentials: true, headers: getAuthHeaders() });
      } else {
        await axios.post(`${API_URL}/api/customer-auth/addresses`, { ...addrForm, is_default: addresses.length === 0 }, { withCredentials: true, headers: getAuthHeaders() });
      }
      const addrRes = await axios.get(`${API_URL}/api/customer-auth/addresses`, { withCredentials: true, headers: getAuthHeaders() });
      setAddresses(addrRes.data.addresses || []);
      setEditingAddr(null);
      setAddrForm({ label: 'Home', address: '', pincode: '' });
    } catch (err) { logger.error('[Portal] Address save error:', err.message); }
    setAddrLoading(false);
  };

  const handleDeleteAddress = async (addressId) => {
    if (!window.confirm("Delete this address?")) return;
    try {
      await axios.delete(`${API_URL}/api/customer-auth/addresses/${addressId}`, { withCredentials: true, headers: getAuthHeaders() });
      setAddresses(prev => prev.filter(a => a.address_id !== addressId));
    } catch (err) { logger.error('[Portal] Address delete error:', err.message); }
  };

  const handleSetDefault = async (addressId) => {
    try {
      await axios.put(`${API_URL}/api/customer-auth/addresses/${addressId}`, { is_default: true }, { withCredentials: true, headers: getAuthHeaders() });
      setAddresses(prev => prev.map(a => ({ ...a, is_default: a.address_id === addressId })));
    } catch (err) { logger.error('[Portal] Set default address error:', err.message); }
  };

  const handlePostBookingAddOns = async (addOns) => {
    if (!addOnBooking || addOns.length === 0) { setAddOnBooking(null); return; }
    setAddOnSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/customer-auth/bookings/${addOnBooking.id}/add-ons`, { add_ons: addOns }, { withCredentials: true, headers: getAuthHeaders() });
      alert("Add-ons added successfully! Our team will coordinate the details.");
      setAddOnBooking(null);
      // Refresh customer data
      const r = await axios.get(`${API_URL}/api/customer-auth/me`, { withCredentials: true, headers: getAuthHeaders() });
      setCustomer(r.data);
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to add add-ons. Please try again.");
    } finally { setAddOnSubmitting(false); }
  };
  const upcomingBookings = (customer.bookings || []).filter(b => {
    const isPast = new Date(b.date) < new Date(new Date().toDateString());
    return b.status !== 'cancelled' && b.booking_status !== 'completed' && !isPast;
  });
  const allBookings = customer.bookings || [];
  const displayName = customer.name || "there";

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FFFAF5] to-[#FFF0E8] relative overflow-hidden flex flex-col">
      <BalloonStyles />
      <LogoWatermark />

      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-[#E83E8C]/10 px-4 py-3 relative z-10">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2"><ArrowLeft size={18} className="text-[#475569]" /><img src={LOGO_URL} alt="DoozyGo" className="h-8" /></Link>
          <button onClick={handleLogout} className="flex items-center gap-1.5 text-sm text-[#475569] hover:text-[#E83E8C] transition-colors" data-testid="customer-logout"><LogOut size={14} /> Logout</button>
        </div>
      </div>

      <div className="flex-1 relative z-10 max-w-2xl mx-auto w-full px-4 py-6 space-y-5">
        <CustomerDashboardHero
          customer={customer} displayName={displayName} allBookings={allBookings}
          editingPhone={editingPhone} setEditingPhone={setEditingPhone}
          editPhoneInput={editPhoneInput} setEditPhoneInput={setEditPhoneInput}
          handleUpdatePhone={handleUpdatePhone}
          balloonProgress={balloonProgress} freePartyEarned={freePartyEarned}
          BalloonIcon={BalloonIcon}
        />

        {/* My Addresses */}
        <AddressManager
          addresses={addresses} editingAddr={editingAddr} setEditingAddr={setEditingAddr}
          addrForm={addrForm} setAddrForm={setAddrForm} addrLoading={addrLoading}
          handleSaveAddress={handleSaveAddress} handleDeleteAddress={handleDeleteAddress}
          handleSetDefault={handleSetDefault}
        />

        {/* My Decorations Gallery + Loyalty */}
        <MyDecorations phone={customer.phone || ""} getAuthHeaders={getAuthHeaders} />

        {/* Unified AI Creations gallery — invitations, decor, creator, party DNA */}
        <MyCreationsTab />

        {/* Smart Invites Dashboard (renders only if customer has at least one invite) */}
        <MyInvitesDashboard />

        <BookingsList
          allBookings={allBookings}
          expandedBookingId={expandedBookingId}
          setExpandedBookingId={setExpandedBookingId}
          onAddAddon={(b) => setAddOnBooking(b)}
          onEditRequest={(b) => setEditRequest(b)}
          onSmartInvite={(b) => setSmartInviteBooking(b)}
          onTextInvite={openInviteModal}
          onCancel={startCancel}
        />

        <CustomerDashboardCTAs />

        <PrivacyPanel phone={customer.phone || ""} />
        {/* iter171 — Account Security card (change phone + rotate session) */}
        <AccountSecurityCard
          authAxios={axios}
          onSessionRotated={handleLogout}
          label="Customer Account Security"
        />
      </div>

      <Footer />

      {/* ════════ Modals ════════ */}
      <CancelBookingModal
        cancelView={cancelView} setCancelView={setCancelView}
        cancelStep={cancelStep} setCancelStep={setCancelStep}
        cancelReason={cancelReason} setCancelReason={setCancelReason}
        cancelCustomReason={cancelCustomReason} setCancelCustomReason={setCancelCustomReason}
        cancelError={cancelError} cancelLoading={cancelLoading} cancelPreview={cancelPreview}
        handleCancelPreview={handleCancelPreview} handleConfirmCancel={handleConfirmCancel}
        fetchProfile={fetchProfile} openFeedbackPopup={openFeedbackPopup}
      />

      <FeedbackModal
        showFeedback={showFeedback} setShowFeedback={setShowFeedback}
        feedbackText={feedbackText} setFeedbackText={setFeedbackText}
        feedbackRating={feedbackRating} setFeedbackRating={setFeedbackRating}
        feedbackSent={feedbackSent} submitFeedback={submitFeedback} fetchProfile={fetchProfile}
      />

      <InviteModal
        inviteBooking={inviteBooking} setInviteBooking={setInviteBooking}
        inviteText={inviteText} setInviteText={setInviteText}
        inviteCopied={inviteCopied} shareInviteWhatsApp={shareInviteWhatsApp}
        shareInviteEmail={shareInviteEmail} copyInviteText={copyInviteText}
      />

      <SmartInviteModal
        open={!!smartInviteBooking}
        onClose={() => setSmartInviteBooking(null)}
        defaults={smartInviteBooking ? {
          booking_id: smartInviteBooking.id,
          occasion: smartInviteBooking.occasion,
          package: smartInviteBooking.package,
          host_name: customer?.name || "",
          event_date: (smartInviteBooking.date || "").split("T")[0],
          event_time: smartInviteBooking.setup_time || "",
          venue: smartInviteBooking.address || "",
        } : {}}
      />

      <EditBookingModal
        editRequest={editRequest} setEditRequest={setEditRequest}
        editChanges={editChanges} setEditChanges={setEditChanges}
        editSubmitting={editSubmitting} handleEditBookingSubmit={handleEditBookingSubmit}
      />

      {addOnBooking && (
        <AddOnPopup
          isOpen={true}
          onClose={() => setAddOnBooking(null)}
          onConfirm={handlePostBookingAddOns}
          selectedPackage={addOnBooking.package}
          packagePrice={0}
          eventDate={addOnBooking.date}
        />
      )}
    </div>
  );
}
